import { AiVerificationError, formatPageLineDisplay } from "../../utils/aiDocumentVerification";
import {
  FILE_VERIFICATION_HEADERS,
  getFileVerificationExportRow,
  getSummaryMetricRows,
} from "./aiVerificationReportFormat";

function escapeCsvCell(value: unknown): string {
  const text = value == null ? "" : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

export function buildCsvContent(headers: string[], rows: unknown[][]): string {
  const headerLine = headers.map(escapeCsvCell).join(",");
  const dataLines = rows.map((row) => row.map(escapeCsvCell).join(","));
  return [headerLine, ...dataLines].join("\r\n");
}

export function downloadCsvFile(filename: string, csvContent: string): void {
  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function formatMentionCell(
  err: AiVerificationError & {
    mention_1?: string;
    mention_2?: string;
  },
  mentionIndex: 1 | 2,
): string {
  const preformatted =
    mentionIndex === 1 ? err.mention_1 : err.mention_2;
  if (preformatted) {
    return String(preformatted);
  }

  const value =
    mentionIndex === 1 ? err.mention1_value : err.mention2_value;
  const quote =
    mentionIndex === 1 ? err.mention1_quote : err.mention2_quote;
  const section =
    mentionIndex === 1 ? err.mention1_section : err.mention2_section;

  if (value && quote) {
    return `${value} - ${quote}`;
  }
  return [value, section || quote].filter(Boolean).join(" ");
}

export function downloadVerificationErrorsCsv(
  errors: Array<
    AiVerificationError & {
      index?: number;
      mention_1?: string;
      mention_2?: string;
    }
  >,
  filename = "system_verification_errors_sample.csv",
): void {
  const headers = [
    "S.No",
    "Section / Field",
    "Error Description",
    "Mention 1",
    "Mention 2",
    "Severity (Mistake Level)",
    "File Name"
  ];

  const rows = errors.map((err, index) => [
    err.index ?? index + 1,
    err.section_field_name || "",
    err.error_description || "",
    formatMentionCell(err, 1),
    formatMentionCell(err, 2),
    err.severity || "",
    err.file_name || "",
  ]);

  downloadCsvFile(filename, buildCsvContent(headers, rows));
}

export function downloadReportSummaryCsv(
  summary: Record<string, unknown> | null,
  fileDetails: Record<string, unknown>[],
  filename = "system_verification_report_summary.csv",
): void {
  const sections: string[] = [];
  const metricRows = getSummaryMetricRows(summary);

  if (metricRows.length > 0) {
    sections.push("SUMMARY METRICS");
    sections.push(buildCsvContent(["Metric", "Value"], metricRows));
    sections.push("");
  }

  const fileRows = fileDetails.map((file) => getFileVerificationExportRow(file));

  sections.push("FILE VERIFICATION STATUS");
  sections.push(buildCsvContent([...FILE_VERIFICATION_HEADERS], fileRows));

  downloadCsvFile(filename, sections.join("\r\n"));
}

/** @deprecated Use downloadReportSummaryCsv */
export function downloadReportSummarySampleCsv(
  summary: Record<string, unknown> | null,
  fileDetails: Record<string, unknown>[],
  filename = "system_verification_report_summary_sample.csv",
): void {
  downloadReportSummaryCsv(summary, fileDetails, filename);
}

export function downloadReportRowsSampleCsv(
  rows: Record<string, unknown>[],
  reportType: string,
  filename?: string,
): void {
  if (!rows.length) {
    return;
  }

  const keys = Object.keys(rows[0]).filter(
    (k) =>
      k !== "raw_response" &&
      k !== "document_url" &&
      k !== "page_number" &&
      k !== "line_number" &&
      k !== "id" &&
      k !== "run_id",
  );
  const orderedKeys = keys.includes("index")
    ? ["index", ...keys.filter((k) => k !== "index")]
    : keys;
  const headers = orderedKeys.map((key) =>
    key === "index"
      ? "S.No"
      : key === "page_line"
        ? "Page No. / Line No."
        : key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
  );
  const dataRows = rows.map((row) => orderedKeys.map((key) => row[key] ?? ""));

  const name =
    filename || `system_verification_report_${reportType}_sample.csv`;
  downloadCsvFile(name, buildCsvContent(headers, dataRows));
}
